import { useEffect, useState } from 'react';
import { Progress } from '@backstage/core-components';
import {
    useApi,
    discoveryApiRef,
    configApiRef,
} from '@backstage/core-plugin-api';
import type {
    SignInPageProps,
    IdentityApi,
    ProfileInfo,
    BackstageUserIdentity,
} from '@backstage/core-plugin-api';
import { setPlatformToken } from '../apis';

function parseBackstageToken(token: string): { sub: string; ent: string[] } {
    const parts = token.split('.');
    if (parts.length !== 3) throw new Error('Invalid backstage token');
    const payload = JSON.parse(atob(parts[1]));
    return { sub: payload.sub, ent: payload.ent || [] };
}

function generateState(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
}

class PlatformTokenIdentity implements IdentityApi {
    private backstageToken: string;
    private userEntityRef: string;
    private ownershipEntityRefs: string[];
    private profile: ProfileInfo;

    constructor(params: {
        backstageToken: string;
        userEntityRef: string;
        ownershipEntityRefs: string[];
        profile: ProfileInfo;
    }) {
        this.backstageToken = params.backstageToken;
        this.userEntityRef = params.userEntityRef;
        this.ownershipEntityRefs = params.ownershipEntityRefs;
        this.profile = params.profile;
    }

    async getProfileInfo(): Promise<ProfileInfo> {
        return this.profile;
    }

    async getBackstageIdentity(): Promise<BackstageUserIdentity> {
        return {
            type: 'user',
            userEntityRef: this.userEntityRef,
            ownershipEntityRefs: this.ownershipEntityRefs,
        };
    }

    async getCredentials(): Promise<{ token?: string }> {
        return { token: this.backstageToken };
    }

    async signOut(): Promise<void> {
        window.location.href = '/';
    }
}

export const PlatformTokenSignInPage = (props: SignInPageProps) => {
    const discoveryApi = useApi(discoveryApiRef);
    const configApi = useApi(configApiRef);
    const [error, setError] = useState<string | undefined>();

    useEffect(() => {
        const authenticate = async () => {
            const clientId = configApi.getOptionalString('app.platformAuth.clientId');
            const metadataUrl = configApi.getOptionalString('app.platformAuth.metadataUrl');

            if (!clientId || !metadataUrl) {
                setError('Missing app.platformAuth.clientId or app.platformAuth.metadataUrl config');
                return;
            }

            const params = new URLSearchParams(window.location.search);

            // State A: Returning from OIDC redirect (URL has code + state)
            if (params.has('code') && params.has('state')) {
                try {
                    // Verify state to prevent CSRF
                    const storedState = sessionStorage.getItem('platform_oidc_state');
                    if (params.get('state') !== storedState) {
                        throw new Error('Invalid state parameter — possible CSRF attack');
                    }
                    sessionStorage.removeItem('platform_oidc_state');

                    const code = params.get('code')!;

                    // Exchange code via backend to avoid CORS (server-to-server)
                    const exchangeUrl = await discoveryApi.getBaseUrl('oidc-token-exchange');
                    const tokenResp = await fetch(`${exchangeUrl}/exchange`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            code,
                            redirect_uri: window.location.origin,
                            client_id: clientId,
                        }),
                    });

                    if (!tokenResp.ok) {
                        const body = await tokenResp.text();
                        throw new Error(`Token exchange failed (${tokenResp.status}): ${body}`);
                    }

                    const tokens = await tokenResp.json();
                    const accessToken = tokens.access_token;

                    if (!accessToken) {
                        throw new Error('No access_token in token response');
                    }

                    // Exchange with backend for Backstage identity token
                    const baseUrl = await discoveryApi.getBaseUrl('auth');
                    const fetchAuth = () => fetch(
                        `${baseUrl}/platform-token/refresh`,
                        {
                            method: 'POST',
                            headers: { 'x-platform-token': accessToken },
                        },
                    );

                    let response = await fetchAuth();
                    for (let attempt = 1; attempt < 5 && response.status === 503; attempt++) {
                        await new Promise(r => setTimeout(r, 2000 * attempt));
                        response = await fetchAuth();
                    }

                    if (!response.ok) {
                        const body = await response.text();
                        throw new Error(
                            `Authentication failed (${response.status}): ${body}`,
                        );
                    }

                    const data = await response.json();

                    const backstageToken = data.backstageIdentity?.token;
                    if (!backstageToken) {
                        throw new Error('No backstage identity token in auth response');
                    }

                    const { sub, ent } = parseBackstageToken(backstageToken);

                    // Store the OIDC access token for API calls
                    setPlatformToken(accessToken);

                    const identity = new PlatformTokenIdentity({
                        backstageToken,
                        userEntityRef: sub,
                        ownershipEntityRefs: ent,
                        profile: {
                            email: data.profile?.email,
                            displayName: data.profile?.displayName,
                        },
                    });

                    // Clean up OIDC params from URL
                    const url = new URL(window.location.href);
                    url.searchParams.delete('code');
                    url.searchParams.delete('state');
                    url.searchParams.delete('session_state');
                    url.searchParams.delete('authContext');
                    window.history.replaceState({}, '', url.toString());

                    props.onSignInSuccess(identity);
                } catch (e: any) {
                    setError(e.message || 'Authentication callback failed');
                }
                return;
            }

            // State B: Initiate OIDC redirect to Platform auth
            try {
                // Derive authorize endpoint from metadataUrl (standard OIDC convention)
                // The redirect is a browser navigation, not a fetch, so no CORS issue
                const authority = metadataUrl.replace('/.well-known/openid-configuration', '');

                const state = generateState();
                sessionStorage.setItem('platform_oidc_state', state);

                const authorizeUrl = new URL(`${authority}/authorize`);
                authorizeUrl.searchParams.set('client_id', clientId);
                authorizeUrl.searchParams.set('redirect_uri', window.location.origin);
                authorizeUrl.searchParams.set('response_type', 'code');
                authorizeUrl.searchParams.set('scope', 'openid profile email');
                authorizeUrl.searchParams.set('state', state);

                // Redirect to Platform auth — page navigates away
                window.location.href = authorizeUrl.toString();
            } catch (e: any) {
                setError(e.message || 'Failed to initiate authentication');
            }
        };

        authenticate();
    }, [discoveryApi, configApi, props]);

    if (error) {
        return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
                <h3>Authentication Error</h3>
                <p>{error}</p>
            </div>
        );
    }

    return <Progress />;
};
